"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  createTimesheetAction,
  deleteTimesheetAction,
} from "@/actions/work-actions";
import {
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Briefcase,
  User,
  Trash2,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  FileText,
} from "lucide-react";

interface TimesheetItem {
  id: string;
  projectId: string;
  projectTitle: string;
  agentId: string;
  agentName: string;
  hours: number;
  date: string;
  description: string | null;
  createdAt: string;
}

interface ProjectItem {
  id: string;
  title: string;
}

interface AgentItem {
  id: string;
  name: string;
}

interface TimesheetsClientProps {
  initialTimesheets: TimesheetItem[];
  projects: ProjectItem[];
  agents: AgentItem[];
}

const MONTHS_IT = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

const DAYS_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export function TimesheetsClient({
  initialTimesheets,
  projects,
  agents,
}: TimesheetsClientProps) {
  const router = useRouter();
  const [timesheetsList, setTimesheetsList] =
    useState<TimesheetItem[]>(initialTimesheets);
  const [projectFilter, setProjectFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");

  // Calendar State
  const [currentDate, setCurrentDate] = useState(new Date());
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Modal State
  const [isLogOpen, setIsLogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Log Form State
  const [logProjectId, setLogProjectId] = useState("");
  const [logAgentId, setLogAgentId] = useState("");
  const [logHours, setLogHours] = useState("");
  const [logDate, setLogDate] = useState("");
  const [logDescription, setLogDescription] = useState("");

  // Sync state with server props
  useEffect(() => {
    setTimesheetsList(initialTimesheets);
  }, [initialTimesheets]);

  // Set default values in logging form if agents/projects exist
  useEffect(() => {
    if (projects.length > 0 && !logProjectId) {
      setLogProjectId(projects[0].id);
    }
    if (agents.length > 0 && !logAgentId) {
      setLogAgentId(agents[0].id);
    }
  }, [projects, agents, logProjectId, logAgentId]);

  const handleOpenLogModal = (preselectedDate?: string) => {
    if (projects.length > 0) setLogProjectId(projects[0].id);
    if (agents.length > 0) setLogAgentId(agents[0].id);
    setLogHours("8");
    setLogDescription("");
    setError("");

    if (preselectedDate) {
      setLogDate(preselectedDate);
    } else {
      const today = new Date().toISOString().substring(0, 10);
      setLogDate(today);
    }

    setIsLogOpen(true);
  };

  const handleLogHours = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!logProjectId) {
      setError("Seleziona un progetto.");
      return;
    }
    if (!logAgentId) {
      setError("Seleziona una risorsa/membro.");
      return;
    }
    const hoursNum = parseFloat(logHours);
    if (isNaN(hoursNum) || hoursNum <= 0 || hoursNum > 24) {
      setError("Inserisci un numero di ore valido (compreso tra 0.1 e 24).");
      return;
    }
    if (!logDate) {
      setError("Seleziona la data di riferimento.");
      return;
    }

    setLoading(true);
    try {
      const res = await createTimesheetAction({
        projectId: logProjectId,
        agentId: logAgentId,
        hours: hoursNum,
        date: logDate,
        description: logDescription || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsLogOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimesheet = async (id: string, projectId: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa registrazione?")) return;
    try {
      const res = await deleteTimesheetAction(id, projectId);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione.");
    }
  };

  // Navigation calendar
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  // Generate monthly calendar grid
  const getDaysInMonth = (y: number, m: number) =>
    new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y: number, m: number) => {
    const day = new Date(y, m, 1).getDay();
    // Monday is start of week index 0, Sunday is 6
    return (day + 6) % 7;
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayIndex = getFirstDayOfMonth(currentYear, currentMonth);

  const calendarDays = [];
  // Empty spaces for previous month days
  for (let i = 0; i < firstDayIndex; i++) {
    calendarDays.push(null);
  }
  // Days of current month
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push(new Date(currentYear, currentMonth, d));
  }

  // Filter timesheets based on dropdown filters
  const filteredTimesheets = timesheetsList.filter((ts) => {
    const matchesProject =
      projectFilter === "all" || ts.projectId === projectFilter;
    const matchesAgent = agentFilter === "all" || ts.agentId === agentFilter;
    return matchesProject && matchesAgent;
  });

  // Calculate sum stats
  const totalHoursLogged = filteredTimesheets.reduce(
    (acc, curr) => acc + curr.hours,
    0,
  );

  // Month-specific stats
  const monthlyTimesheets = filteredTimesheets.filter((ts) => {
    const d = new Date(ts.date);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  });
  const monthlyHoursLogged = monthlyTimesheets.reduce(
    (acc, curr) => acc + curr.hours,
    0,
  );

  // Project breakdown statistics
  const projectBreakdown: { [key: string]: number } = {};
  filteredTimesheets.forEach((ts) => {
    projectBreakdown[ts.projectTitle] =
      (projectBreakdown[ts.projectTitle] || 0) + ts.hours;
  });
  const projectBreakdownSorted = Object.entries(projectBreakdown).sort(
    (a, b) => b[1] - a[1],
  );

  // Helper to find hours logged on a specific day in the current month
  const getLoggedHoursForDay = (date: Date) => {
    const checkStr = date.toISOString().substring(0, 10);
    const dayTimesheets = filteredTimesheets.filter(
      (ts) => ts.date.substring(0, 10) === checkStr,
    );
    return dayTimesheets.reduce((acc, curr) => acc + curr.hours, 0);
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Ore Totali Loggate
            </span>
            <Clock className="h-4 w-4 text-primary-500" />
          </div>
          <p className="text-2xl font-bold mt-2">{totalHoursLogged} h</p>
          <p className="text-xs text-text-secondary mt-1">
            Tempo cumulativo sui progetti attivi
          </p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
              Ore di {MONTHS_IT[currentMonth]}
            </span>
            <TrendingUp className="h-4 w-4 text-success" />
          </div>
          <p className="text-2xl font-bold mt-2 text-success">
            {monthlyHoursLogged} h
          </p>
          <p className="text-xs text-text-secondary mt-1">
            Registrate nel mese corrente
          </p>
        </Card>

        <Card className="p-4 flex flex-col justify-center">
          <Button
            onClick={() => handleOpenLogModal()}
            className="w-full gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Registra Ore Lavoro
          </Button>
        </Card>
      </div>

      {/* Filter and Content layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Logger (Columns 1 & 2) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Calendar Card */}
          <Card className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border/40">
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevMonth}
                  className="p-1.5 hover:bg-card-muted rounded-lg border border-border"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <h3 className="text-base font-bold text-text-primary">
                  {MONTHS_IT[currentMonth]} {currentYear}
                </h3>
                <button
                  onClick={handleNextMonth}
                  className="p-1.5 hover:bg-card-muted rounded-lg border border-border"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Filters inline in calendar */}
              <div className="flex gap-2">
                <Select
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="h-8 text-xs w-[150px]"
                >
                  <option value="all">Tutti i progetti</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>

                <Select
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  className="h-8 text-xs w-[140px]"
                >
                  <option value="all">Tutti i membri</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2 pt-4 text-center">
              {DAYS_SHORT.map((day) => (
                <span
                  key={day}
                  className="text-xs font-semibold text-text-muted"
                >
                  {day}
                </span>
              ))}

              {calendarDays.map((date, idx) => {
                if (!date) {
                  return (
                    <div
                      key={`empty-${idx}`}
                      className="aspect-square border border-transparent rounded-lg bg-card-muted/5 opacity-20"
                    />
                  );
                }

                const dayHours = getLoggedHoursForDay(date);
                const isoStr = date.toISOString().substring(0, 10);
                const isToday =
                  new Date().toDateString() === date.toDateString();

                return (
                  <button
                    key={isoStr}
                    onClick={() => handleOpenLogModal(isoStr)}
                    className={`aspect-square border rounded-lg p-1.5 flex flex-col justify-between items-center transition-all cursor-pointer group hover:border-primary-500/50 hover:bg-card-hover/40 ${
                      isToday
                        ? "border-primary-600 bg-primary-500/5 shadow-xs"
                        : "border-border/60 bg-card"
                    }`}
                  >
                    <span
                      className={`text-xs font-semibold ${
                        isToday
                          ? "text-primary-600 font-bold"
                          : "text-text-primary"
                      }`}
                    >
                      {date.getDate()}
                    </span>

                    {dayHours > 0 ? (
                      <Badge
                        variant={dayHours >= 8 ? "success" : "info"}
                        className="px-1 text-[9px] py-0 font-bold scale-90 sm:scale-100"
                      >
                        {dayHours}h
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-text-muted opacity-0 group-hover:opacity-100 transition-opacity">
                        + log
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Recent Entries List */}
          <Card className="p-4">
            <CardHeader className="p-0 pb-3 border-b border-border/40">
              <CardTitle className="text-sm font-bold text-text-primary">
                Registrazioni Recenti
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-3">
              {filteredTimesheets.length === 0 ? (
                <div className="p-8 text-center text-text-muted">
                  Nessuna registrazione di ore trovata per i criteri
                  selezionati.
                </div>
              ) : (
                <div className="divide-y divide-border/40 overflow-y-auto max-h-[300px] scrollbar-thin">
                  {filteredTimesheets
                    .sort(
                      (a, b) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime(),
                    )
                    .map((ts) => (
                      <div
                        key={ts.id}
                        className="py-3 flex items-center justify-between gap-4 text-xs group"
                      >
                        <div className="space-y-1 truncate">
                          <div className="flex items-center gap-1.5 font-semibold text-text-primary">
                            <span className="bg-card-muted px-1.5 py-0.5 rounded border border-border font-mono text-[10px]">
                              {new Date(ts.date).toLocaleDateString("it-IT", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                            <Link
                              href={`/explore?nodeId=${ts.projectId}`}
                              className="truncate max-w-[140px] md:max-w-[200px] hover:text-primary-600 hover:underline transition-colors font-semibold"
                              title={ts.projectTitle}
                            >
                              {ts.projectTitle}
                            </Link>
                          </div>
                          <div className="flex items-center gap-2 text-text-secondary">
                            <Link
                              href={`/explore?nodeId=${ts.agentId}`}
                              className="flex items-center gap-1 hover:text-primary-600 hover:underline transition-colors"
                            >
                              <User className="h-3 w-3 shrink-0" />
                              {ts.agentName}
                            </Link>
                            {ts.description && (
                              <span
                                className="italic truncate max-w-[200px] md:max-w-[300px]"
                                title={ts.description}
                              >
                                &ldquo;{ts.description}&rdquo;
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <Badge
                            variant="neutral"
                            className="bg-primary-500/10 text-primary-700 border-primary-500/20 font-bold font-mono"
                          >
                            {ts.hours} h
                          </Badge>
                          <button
                            onClick={() =>
                              handleDeleteTimesheet(ts.id, ts.projectId)
                            }
                            className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Project Breakdown Panel (Column 3) */}
        <div className="space-y-6">
          <Card className="p-4">
            <CardHeader className="p-0 pb-3 border-b border-border/40">
              <CardTitle className="text-sm font-bold text-text-primary">
                Allocazione per Progetto
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-4 space-y-4">
              {projectBreakdownSorted.length === 0 ? (
                <div className="p-6 text-center text-text-muted text-xs">
                  Nessun dato di allocazione ore disponibile.
                </div>
              ) : (
                projectBreakdownSorted.map(([projTitle, hours]) => {
                  const percent =
                    totalHoursLogged > 0
                      ? Math.round((hours / totalHoursLogged) * 100)
                      : 0;
                  return (
                    <div key={projTitle} className="space-y-1.5">
                      <div className="flex justify-between items-center text-xs">
                        <span
                          className="font-semibold text-text-primary truncate max-w-[160px]"
                          title={projTitle}
                        >
                          {projTitle}
                        </span>
                        <span className="text-text-muted font-mono font-semibold">
                          {hours} h ({percent}%)
                        </span>
                      </div>
                      <div className="w-full bg-border rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-primary-600 h-1.5 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          <Card className="p-4 bg-primary-500/5 border border-primary-500/20">
            <div className="flex gap-3">
              <div className="p-2 bg-primary-500/10 rounded-lg text-primary-600 h-fit shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <h4 className="text-xs font-bold text-text-primary">
                  Consiglio Operativo
                </h4>
                <p className="text-xs text-text-secondary leading-relaxed">
                  Logga le tue ore giornalmente per garantire report di budget
                  precisi per i clienti e consentire ai sistemi AI di
                  ottimizzare le stime dei task successivi.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Log Hours Dialog */}
      <Dialog open={isLogOpen} onOpenChange={setIsLogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registra Ore Lavoro</DialogTitle>
            <DialogDescription>
              Inserisci le ore dedicate alle attività operative del progetto.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleLogHours} className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="log-project">Progetto</Label>
                <Select
                  id="log-project"
                  value={logProjectId}
                  onChange={(e) => setLogProjectId(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Seleziona progetto...</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="log-agent">Membro / Risorsa</Label>
                <Select
                  id="log-agent"
                  value={logAgentId}
                  onChange={(e) => setLogAgentId(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Seleziona membro...</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="log-hours">Ore Lavorate (h)</Label>
                <Input
                  id="log-hours"
                  type="number"
                  step="0.5"
                  min="0.1"
                  max="24"
                  placeholder="es. 8"
                  value={logHours}
                  onChange={(e) => setLogHours(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="log-date">Data Riferimento</Label>
                <Input
                  id="log-date"
                  type="date"
                  value={logDate}
                  onChange={(e) => setLogDate(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="log-desc">Descrizione Attività (Opzionale)</Label>
              <textarea
                id="log-desc"
                placeholder="Descrivi brevemente il lavoro svolto..."
                value={logDescription}
                onChange={(e) => setLogDescription(e.target.value)}
                disabled={loading}
                className="flex min-h-[80px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsLogOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Registra Ore
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
